import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { reviews } from '@/mocks/categoryData';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9q1dp6e3oq4jqumtf70';

export default function AboutPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const body = new URLSearchParams({
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        phone: formData.get('phone') as string,
        message: formData.get('message') as string,
      }).toString();

      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '';
        setFormStatus('error');
        setFormError(msg || t('about.contact.form.error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(t('about.contact.form.error'));
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">{t('breadcrumb.home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 font-medium">{t('about.title')}</span>
          </nav>

          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{t('about.title')}</h1>
          </div>

          {/* Brand Introduction */}
          <section className="max-w-4xl mx-auto mb-16">
            <h2 className="text-xl font-bold text-foreground-950 mb-5">{t('about.brand.title')}</h2>
            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4">
              {t('about.brand.content').split('\n\n').map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </section>

          {/* Reputation Stats */}
          <section className="max-w-4xl mx-auto mb-16">
            <h2 className="text-xl font-bold text-foreground-950 text-center mb-3">{t('about.reputation.title')}</h2>
            <p className="text-sm text-foreground-600 text-center mb-8">{t('about.reputation.subtitle')}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
              {[
                { value: '98%', label: t('about.reputation.rating'), icon: 'ri-heart-line', color: 'rose' },
                { value: '500万+', label: t('about.reputation.users'), icon: 'ri-user-line', color: 'amber' },
                { value: '12.8万', label: t('about.reputation.count'), icon: 'ri-thumb-up-line', color: 'emerald' },
                { value: '36项', label: t('about.reputation.awards'), icon: 'ri-award-line', color: 'cyan' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-5 rounded-xl bg-background-100">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100/80">
                    <i className={`${stat.icon} text-secondary-500 text-xl`}></i>
                  </div>
                  <div className="text-2xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Reviews */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reviews.map((review, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100/70 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-500">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-foreground-950 text-center mb-3">{t('about.contact.title')}</h2>
            <p className="text-sm text-foreground-600 text-center mb-8">{t('about.contact.subtitle')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-4">
                {[
                  { icon: 'ri-map-pin-line', label: t('about.contact.address.label'), value: t('about.contact.address.value') },
                  { icon: 'ri-phone-line', label: t('about.contact.phone.label'), value: t('about.contact.phone.value') },
                  { icon: 'ri-mail-line', label: t('about.contact.email.label'), value: t('about.contact.email.value') },
                  { icon: 'ri-time-line', label: t('about.contact.hours.label'), value: t('about.contact.hours.value') },
                  { icon: 'ri-wechat-line', label: t('about.contact.wechat.label'), value: t('about.contact.wechat.value') },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3 p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-100/70">
                      <i className={`${item.icon} text-primary-500`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <div className="p-6 rounded-xl bg-background-100">
                <form onSubmit={handleContactSubmit} data-readdy-form>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                  />
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">{t('about.contact.form.name')}</label>
                      <input
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">{t('about.contact.form.email')}</label>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">{t('about.contact.form.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">{t('about.contact.form.message')}</label>
                      <textarea
                        name="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      />
                      <div className="text-xs text-foreground-400 mt-1 text-right">{message.length}/500</div>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      {t('about.contact.form.submit')}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-emerald-600 text-center">{t('about.contact.form.success')}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}